<template>
  <div class="dpo">
    <h2 class="dpo_title">События по работе с просрочкой</h2>

    <data-loading :loading="loading" />

    <template v-if="!loading">
      <div v-if="notInvested" class="dpo_empty">
        Этот раздел доступен только инвесторам проекта.
      </div>

      <div v-else-if="error" class="dpo_error">
        Не удалось загрузить события по работе с просрочкой. Попробуйте обновить страницу позже.
      </div>

      <div v-else-if="timeline.length" class="dpo_timeline">
        <div
            v-for="(event, index) in timeline"
            :key="index"
            class="dpo_event"
            :class="`dpo_event--${event.status}`"
        >
          <div class="dpo_marker">
            <span class="dpo_dot"></span>
          </div>
          <div class="dpo_content">
            <div class="dpo_event_head">
              <span class="dpo_event_title"><linkified-text :text="event.title" /></span>
              <span v-if="event.date" class="dpo_event_date">{{ event.date }}</span>
            </div>
            <ul v-if="event.details && event.details.length" class="dpo_event_details">
              <li v-for="(detail, detailIndex) in event.details" :key="detailIndex"><linkified-text :text="detail" /></li>
            </ul>
          </div>
        </div>
      </div>

      <div v-else class="dpo_empty">
        По данному проекту событий по работе с просрочкой пока нет.
      </div>
    </template>
  </div>
</template>

<script>
import moment from "moment";
import { DataLoading } from "@/components/common";
import LinkifiedText from "@/components/common/components/LinkifiedText.vue";
import { createOverdueEventProvider } from "@/utils/di";

function formatDate(date) {
  return date ? moment(date).format("DD.MM.YYYY") : "";
}

function normalizeStatus(status) {
  const s = String(status || "").toLowerCase();
  if (["future", "pending", "planned", "new", "waiting", "upcoming", "scheduled", "todo"].includes(s)) {
    return "future";
  }
  if (["current", "in_progress", "inprogress", "in-progress", "active", "progress", "ongoing"].includes(s)) {
    return "current";
  }
  return "done";
}

export default {
  name: "DetailedProjectOverdue",
  components: {
    DataLoading,
    LinkifiedText,
  },
  props: {
    project: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      events: [],
      loading: false,
      error: false,
      notInvested: false,
    };
  },
  computed: {
    timeline() {
      return this.events.map((event) => ({
        status: normalizeStatus(event.status),
        title: event.name,
        date: formatDate(event.date),
        details: (event.actions || []).map((action) => {
          const actionDate = formatDate(action.date);
          return actionDate ? `${action.name} (${actionDate})` : action.name;
        }),
      }));
    },
  },
  async mounted() {
    await this.loadEvents();
  },
  methods: {
    async loadEvents() {
      this.loading = true;
      this.error = false;
      this.notInvested = false;
      try {
        const provider = await createOverdueEventProvider();
        const collection = await provider.getList(this.project.uuid);
        this.events = collection.items || [];
      } catch (e) {
        if (e?.response?.status === 404) {
          this.notInvested = true;
          return;
        }
        this.error = true;
        throw e;
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.dpo {
  padding: 0.5rem 0;
}

.dpo_title {
  font-size: 1.35rem;
  font-weight: 600;
  color: var(--font-dark-color);
  margin-bottom: 0.75rem;
}

.dpo_timeline {
  position: relative;
}

.dpo_event {
  position: relative;
  display: flex;
  gap: 1rem;
  padding-bottom: 1.5rem;

  &:last-child {
    padding-bottom: 0;
  }
}

.dpo_marker {
  position: relative;
  flex: 0 0 auto;
  display: flex;
  justify-content: center;
  width: 1.25rem;
}

// вертикальная соединительная линия между точками
.dpo_event:not(:last-child) .dpo_marker:after {
  content: "";
  position: absolute;
  top: 1.4rem;
  bottom: -1.5rem;
  left: 50%;
  transform: translateX(-50%);
  width: 2px;
}

.dpo_dot {
  position: relative;
  z-index: 1;
  width: 1rem;
  height: 1rem;
  border-radius: 50%;
  margin-top: 0.3rem;
}

.dpo_content {
  flex: 1 1 auto;
  min-width: 0;
}

.dpo_event_head {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 0.35rem;
}

.dpo_event_title {
  font-size: 1.1rem;
  font-weight: 600;
}

.dpo_event_date {
  flex: 0 0 auto;
  white-space: nowrap;
}

.dpo_event_details {
  list-style: disc;
  display: flex;
  flex-direction: column;
  gap: 0.3rem;
  margin: 0;
  padding-left: 1.25rem;

  li {
    line-height: 1.4;
  }
}

.dpo_event--future {
  .dpo_dot {
    background: var(--stroke-on-white-color);
  }

  .dpo_marker:after {
    background: var(--stroke-on-white-color);
  }

  .dpo_event_title,
  .dpo_event_date {
    color: var(--secondary-gray);
  }

  .dpo_event_details {
    color: var(--font-medium-gray-color);
  }
}

.dpo_event--current {
  .dpo_dot {
    width: 1.25rem;
    height: 1.25rem;
    margin-top: 0.2rem;
    background: var(--primary-green-color);
    box-shadow: 0 0 0 0.25rem rgba(5, 183, 104, 0.18);
  }

  .dpo_marker:after {
    background: var(--primary-green-color);
  }

  .dpo_event_title {
    color: var(--font-dark-color);
    font-size: 1.2rem;
  }

  .dpo_event_date {
    color: var(--font-dark-color);
  }

  .dpo_event_details {
    color: var(--font-dark-color);
  }
}

.dpo_event--done {
  .dpo_dot {
    background: var(--primary-green-color);
  }

  .dpo_marker:after {
    background: var(--primary-green-color);
  }

  .dpo_event_title,
  .dpo_event_date,
  .dpo_event_details {
    color: var(--font-dark-color);
  }
}

.dpo_empty {
  color: var(--secondary-gray);
  padding: 1rem 0;
}

.dpo_error {
  color: var(--red-error-color, #dc3545);
  padding: 1rem 0;
}
</style>
